/**
 * Parses an existing `IF ...` line into the wizard's AST.
 *
 * Approach: hand-written tokenizer + recursive-descent parser. Mirrors the
 * operator catalogue in `grammar.ts` (which is sourced from
 * Backend\DataQ_Engine\Engine_Utils\MetaQ_PremiseEval.cpp:228-265).
 *
 * Tolerant: partial parses are returned with collected errors, so the wizard
 * can still surface what it understood and let the user edit the rest.
 */

import {
  ASTNode,
  ComparisonOp,
  ComparisonNode,
  GroupNode,
  MembershipOp,
  MembershipNode,
  NotNode,
  Operand,
  QRefOperand,
  ARefOperand,
  NumOperand,
  SetOperand,
  CountOperand,
  WhereOperand,
  COMPARISON_OPS,
  MEMBERSHIP_OPS,
  isReservedWord,
} from "./grammar"

export interface ParseResult {
  ast: ASTNode
  errors: string[]
}

// ─── Tokenizer ───────────────────────────────────────────────────────────────

type TokenKind =
  | "label"        // <<...>>
  | "ident"        // bare word
  | "number"       // 5, 5.5, -1
  | "lparen"       // (
  | "rparen"       // )
  | "lbrace"       // {
  | "rbrace"       // }
  | "comma"        // ,
  | "op"           // operator (single or multi-word, e.g. "IS NOT", "ANY OF", "<=", "<")

interface Token {
  kind: TokenKind
  value: string
  pos: number
}

const SINGLE_CHAR_OPS = ["<=", ">=", "<>", "!=", "=", "<", ">"]

const MULTIWORD_OPS = [
  "IS NOT",
  "NOT IN",
  "ANY OF",
  "ALL OF",
  "NONE OF",
] as const

const SINGLE_WORD_OPS = ["IS", "IN", "AND", "OR", "NOT", "WHERE", "PER", "SELECT"]

function tokenize(input: string): Token[] {
  const tokens: Token[] = []
  let i = 0

  while (i < input.length) {
    const ch = input[i]

    // Whitespace
    if (/\s/.test(ch)) {
      i++
      continue
    }

    // Quoted label `<<...>>`
    if (ch === "<" && input[i + 1] === "<") {
      const end = input.indexOf(">>", i + 2)
      if (end < 0) {
        // Unterminated label — consume rest as label content, mark error later.
        tokens.push({ kind: "label", value: input.slice(i + 2), pos: i })
        i = input.length
        break
      }
      tokens.push({ kind: "label", value: input.slice(i + 2, end), pos: i })
      i = end + 2
      continue
    }

    // Brackets / commas
    if (ch === "(") { tokens.push({ kind: "lparen", value: "(", pos: i }); i++; continue }
    if (ch === ")") { tokens.push({ kind: "rparen", value: ")", pos: i }); i++; continue }
    if (ch === "{") { tokens.push({ kind: "lbrace", value: "{", pos: i }); i++; continue }
    if (ch === "}") { tokens.push({ kind: "rbrace", value: "}", pos: i }); i++; continue }
    if (ch === ",") { tokens.push({ kind: "comma", value: ",", pos: i }); i++; continue }

    // Multi-char operators
    let matched = false
    for (const op of SINGLE_CHAR_OPS) {
      if (input.startsWith(op, i)) {
        tokens.push({ kind: "op", value: op, pos: i })
        i += op.length
        matched = true
        break
      }
    }
    if (matched) continue

    // Numbers (support leading minus only when not directly after an ident/number/label/rparen)
    if (/[0-9]/.test(ch) || (ch === "-" && /[0-9.]/.test(input[i + 1] ?? ""))) {
      const prev = tokens[tokens.length - 1]
      const isUnaryMinus =
        ch === "-" &&
        (!prev ||
          prev.kind === "op" ||
          prev.kind === "lparen" ||
          prev.kind === "lbrace" ||
          prev.kind === "comma")
      if (ch !== "-" || isUnaryMinus) {
        const m = input.slice(i).match(/^-?\d+(?:\.\d+)?/)
        if (m) {
          tokens.push({ kind: "number", value: m[0], pos: i })
          i += m[0].length
          continue
        }
      }
    }

    // Identifier / keyword
    if (/[A-Za-z_]/.test(ch)) {
      const m = input.slice(i).match(/^[A-Za-z_][A-Za-z0-9_]*/)
      if (m) {
        const word = m[0]
        const upper = word.toUpperCase()
        // Try multi-word ops greedily by peeking ahead.
        const rest = input.slice(i + word.length).replace(/^\s+/, "")
        let consumed = false
        for (const mw of MULTIWORD_OPS) {
          const [first, second] = mw.split(" ")
          if (upper === first) {
            const second_m = rest.match(/^[A-Za-z_]+/)
            if (second_m && second_m[0].toUpperCase() === second) {
              tokens.push({ kind: "op", value: mw, pos: i })
              i += word.length + (input.slice(i + word.length).length - rest.length) + second_m[0].length
              consumed = true
              break
            }
          }
        }
        if (consumed) continue

        // Single-word op?
        if (SINGLE_WORD_OPS.includes(upper)) {
          tokens.push({ kind: "op", value: upper, pos: i })
          i += word.length
          continue
        }

        // COUNT — function call follows
        if (upper === "COUNT") {
          tokens.push({ kind: "ident", value: "COUNT", pos: i })
          i += word.length
          continue
        }

        tokens.push({ kind: "ident", value: word, pos: i })
        i += word.length
        continue
      }
    }

    // Unknown character — skip and continue, parser will surface an error.
    i++
  }

  return tokens
}

// ─── Parser ──────────────────────────────────────────────────────────────────

class Parser {
  private pos = 0
  readonly errors: string[] = []

  constructor(private tokens: Token[]) {}

  private peek(offset = 0): Token | undefined {
    return this.tokens[this.pos + offset]
  }

  private consume(): Token | undefined {
    return this.tokens[this.pos++]
  }

  private match(kind: TokenKind, value?: string): boolean {
    const t = this.peek()
    if (!t) return false
    if (t.kind !== kind) return false
    if (value !== undefined && t.value.toUpperCase() !== value.toUpperCase()) return false
    return true
  }

  private expect(kind: TokenKind, value?: string): Token | null {
    if (!this.match(kind, value)) {
      const got = this.peek()
      this.errors.push(
        `Expected ${value ?? kind}, got ${got ? got.value : "end of input"}`,
      )
      return null
    }
    return this.consume()!
  }

  // expression  := orExpr
  // orExpr      := andExpr (OR andExpr)*
  // andExpr     := unary (AND unary)*
  // unary       := NOT unary | primary
  // primary     := '(' expression ')' | comparison
  // comparison  := operand cmpOp operand
  //             |  operand membershipOp setLiteral
  // operand     := COUNT '(' argument ')' | label | ident | number | whereExpr
  // whereExpr   := operand WHERE [PER|SELECT] operand
  parseExpression(): ASTNode {
    return this.parseOr()
  }

  private parseOr(): ASTNode {
    const left = this.parseAnd()
    const children: ASTNode[] = [left]
    while (this.match("op", "OR")) {
      this.consume()
      children.push(this.parseAnd())
    }
    if (children.length === 1) return left
    return { kind: "group", op: "OR", children } satisfies GroupNode
  }

  private parseAnd(): ASTNode {
    const left = this.parseUnary()
    const children: ASTNode[] = [left]
    while (this.match("op", "AND")) {
      this.consume()
      children.push(this.parseUnary())
    }
    if (children.length === 1) return left
    return { kind: "group", op: "AND", children } satisfies GroupNode
  }

  private parseUnary(): ASTNode {
    if (this.match("op", "NOT")) {
      this.consume()
      const child = this.parseUnary()
      return { kind: "not", child } satisfies NotNode
    }
    return this.parsePrimary()
  }

  private parsePrimary(): ASTNode {
    if (this.match("lparen")) {
      this.consume()
      const inner = this.parseExpression()
      this.expect("rparen")
      return inner
    }
    return this.parseComparison()
  }

  private parseComparison(): ASTNode {
    const leftRaw = this.parseOperand()
    // The LHS of a comparison/membership is always a question reference
    // (or COUNT/WHERE wrapping one). Coerce by position rather than guessing
    // from the label's casing.
    const left = coerceLeftToQref(leftRaw)
    const opTok = this.peek()
    if (!opTok || opTok.kind !== "op") {
      this.errors.push("Expected operator after operand")
      return { kind: "placeholder" }
    }
    const opUpper = opTok.value.toUpperCase()
    // The engine accepts `!=` as a synonym of `<>`; normalise so the AST
    // (and the UI dropdown) only carries one canonical "not-equal" operator.
    const canonicalOp: ComparisonOp | null =
      opUpper === "!="
        ? "<>"
        : COMPARISON_OPS.includes(opUpper as ComparisonOp)
          ? (opUpper as ComparisonOp)
          : null
    if (canonicalOp !== null) {
      this.consume()
      const right = this.parseOperand()
      return {
        kind: "cmp",
        left,
        op: canonicalOp,
        right,
      } satisfies ComparisonNode
    }
    if (MEMBERSHIP_OPS.includes(opUpper as MembershipOp)) {
      this.consume()
      const right = this.parseSetOrOperand()
      // Membership requires a set literal on the right — coerce single operand into a 1-elem set.
      const setRight: SetOperand =
        right.kind === "set"
          ? right
          : { kind: "set", items: [right as Operand] }
      return {
        kind: "set-cmp",
        left,
        op: opUpper as MembershipOp,
        right: setRight,
      } satisfies MembershipNode
    }
    this.errors.push(`Unexpected operator ${opTok.value}`)
    return { kind: "placeholder" }
  }

  private parseOperand(): Operand {
    const t = this.peek()
    if (!t) {
      this.errors.push("Expected operand, got end of input")
      return { kind: "aref", label: "" }
    }

    // COUNT(...)
    if (t.kind === "ident" && t.value.toUpperCase() === "COUNT") {
      this.consume()
      this.expect("lparen")
      const arg = this.parseOperand()
      // COUNT's arg may carry WHERE — handled below in WHERE check.
      const withWhere = this.tryParseWhere(arg)
      this.expect("rparen")
      const argFinal = coerceCountArg(withWhere)
      return { kind: "count", arg: argFinal } satisfies CountOperand
    }

    // Operands default to `aref` (a value reference); the LHS of a comparison
    // gets coerced to `qref` in parseComparison. Classifying by position is
    // safer than guessing from the label's casing, since real question labels
    // can be lowercase (`Q test:`) or mixed-case.
    let base: Operand
    if (t.kind === "label") {
      this.consume()
      base = { kind: "aref", label: t.value } satisfies ARefOperand
    } else if (t.kind === "ident") {
      this.consume()
      if (isReservedWord(t.value)) {
        this.errors.push(`Identifier "${t.value}" is a reserved word`)
      }
      base = { kind: "aref", label: t.value } satisfies ARefOperand
    } else if (t.kind === "number") {
      this.consume()
      base = { kind: "num", value: parseFloat(t.value) } satisfies NumOperand
    } else if (t.kind === "lbrace") {
      base = this.parseSet()
    } else {
      this.errors.push(`Unexpected token ${t.value}`)
      this.consume()
      return { kind: "aref", label: "" }
    }

    // WHERE (only valid on qref/aref/count)
    return this.tryParseWhere(base)
  }

  private tryParseWhere(base: Operand): Operand {
    if (this.match("op", "WHERE")) {
      this.consume()
      let axis: "PER" | "SELECT" | undefined
      if (this.match("op", "PER")) {
        this.consume()
        axis = "PER"
      } else if (this.match("op", "SELECT")) {
        this.consume()
        axis = "SELECT"
      }
      const key = this.parseOperand()
      // base must be a question reference for WHERE to apply meaningfully
      const question: QRefOperand =
        base.kind === "qref"
          ? base
          : base.kind === "aref"
            ? { kind: "qref", label: base.label }
            : { kind: "qref", label: "" }
      const keyOperand: ARefOperand | NumOperand =
        key.kind === "aref" || key.kind === "num"
          ? key
          : key.kind === "qref"
            ? { kind: "aref", label: key.label }
            : { kind: "aref", label: "" }
      return { kind: "where", question, axis, key: keyOperand } satisfies WhereOperand
    }
    return base
  }

  private parseSetOrOperand(): Operand {
    if (this.match("lbrace")) return this.parseSet()
    return this.parseOperand()
  }

  private parseSet(): SetOperand {
    this.expect("lbrace")
    const items: Operand[] = []
    if (!this.match("rbrace")) {
      items.push(this.parseSetItem())
      while (this.match("comma")) {
        this.consume()
        items.push(this.parseSetItem())
      }
    }
    this.expect("rbrace")
    return { kind: "set", items } satisfies SetOperand
  }

  private parseSetItem(): Operand {
    const t = this.peek()
    if (!t) {
      this.errors.push("Expected set item")
      return { kind: "aref", label: "" }
    }
    if (t.kind === "label") {
      this.consume()
      return { kind: "aref", label: t.value }
    }
    if (t.kind === "number") {
      this.consume()
      return { kind: "num", value: parseFloat(t.value) }
    }
    if (t.kind === "ident") {
      this.consume()
      if (isReservedWord(t.value)) {
        this.errors.push(`Set item "${t.value}" is a reserved word`)
      }
      return { kind: "aref", label: t.value }
    }
    this.errors.push(`Unexpected set item ${t.value}`)
    this.consume()
    return { kind: "aref", label: "" }
  }
}

/**
 * The LHS of a comparison or set-membership refers to a question (or a
 * COUNT/WHERE wrapping one). Coerce by position: any `aref` we parsed on
 * the left becomes a `qref` with the same label.
 */
function coerceLeftToQref(o: Operand): Operand {
  switch (o.kind) {
    case "aref":
      return { kind: "qref", label: o.label }
    case "qref":
    case "count":
    case "where":
    case "num":
    case "set":
      return o
  }
}

/**
 * COUNT() takes a question reference (optionally wrapped in WHERE). Coerce
 * a parsed `aref` into a `qref` so the AST shape is right.
 */
function coerceCountArg(o: Operand): QRefOperand | WhereOperand {
  if (o.kind === "qref" || o.kind === "where") return o
  if (o.kind === "aref") return { kind: "qref", label: o.label }
  return { kind: "qref", label: "" }
}

/**
 * Parse an `IF ...` line (with or without the leading `IF` keyword).
 */
export function parseIfLine(input: string): ParseResult {
  const trimmed = input.trim().replace(/^IF\s+/i, "")
  if (!trimmed) {
    return { ast: { kind: "placeholder" }, errors: [] }
  }
  const tokens = tokenize(trimmed)
  const parser = new Parser(tokens)
  const ast = parser.parseExpression()
  // If there are leftover tokens, surface as a soft error.
  return { ast, errors: parser.errors }
}
